import { useDispatch } from "react-redux";
import {
  fetchSubmissionsAction,
  fetchGroupSubmissionsAction,
  fetchPracticeSubmissionsAction,
} from "store/actions/submissions";
import { toast } from "react-toastify";
import { useCurrentUser, useLogout } from "utils/hooks";
import { useCallback, useRef } from "react";

// Helper function to safely process chunked data
const processChunkedData = (data: any) => {
  try {
    if (!data || !data.chunks || !Array.isArray(data.chunks)) {
      console.error("Invalid chunked data format:", data);
      return null;
    }

    // Reconstruct the full array from chunks
    return data.chunks.flat();
  } catch (error) {
    console.error("Error processing chunked data:", error);
    return null;
  }
};

const useFetchSubmissions = () => {
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { user } = useCurrentUser();
  const lastFetchTime = useRef<number>(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  const fetchAllSubmissions = useCallback(
    (orgSwitched?: boolean) => {
      // Check if we've fetched recently and use cached data if available
      const now = Date.now();
      if (now - lastFetchTime.current < CACHE_DURATION && !orgSwitched) {
        return Promise.resolve();
      }

      lastFetchTime.current = now;

      return new Promise(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              query {
                getAllSubmissionsByCreatorId {
                  uuid
                  createdAt
                  slug
                  userId
                  authorId
                  assignmentId
                  assignmentLabel
                  privacy
                  color
                  isDraft
                  groupId
                  groupName
                  groupPrivacy
                  isWideData
                  category
                  hideFromPatient
                  userName
                  userEmail
                  therapistId
                  isNegative
                  sources
                  icon {
                    name
                    src
                  }
                  responseFields {
                    uuid
                    submissionId
                    sectionId
                    sectionLabel
                    sectionOrder
                    fieldId
                    fieldLabel
                    fieldType
                    value
                    includeInScoreCalculation
                  }
                  submissionScore {
                    scoringInstructions
                    scoringType
                    totalScore
                    color
                    fieldsetScores {
                      name
                      score
                      scoringType
                    }
                    scoreDisplays {
                        id
                        name
                        min
                        max
                        color
                        order
                    }
                    scoreSuffix
                    scoreAsPercentage
                    minPossibleScore
                    maxPossibleScore
                  }
                }
              }
          `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "read_submissions",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (
                error.errors &&
                error.errors[0] &&
                error.errors[0].message.includes("BRKN_6001")
              ) {
                toast.warn(`For security purposes please log in again.`);
                logOut();
              } else {
                toast.error(`Hmm, something went wrong.`);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong (Code: 09AIMN). ${resData.errors[0].message}`
              );
            } else {
              try {
                // Handle chunked responses
                const submissions = resData.data.getAllSubmissionsByCreatorId;
                if (submissions.isChunked) {
                  // Process chunked data
                  const fullSubmissions = processChunkedData(submissions);

                  if (fullSubmissions) {
                    dispatch(fetchSubmissionsAction(fullSubmissions));
                    Promise.resolve(fullSubmissions).then((r) => r);
                  } else {
                    console.error("Failed to process chunked submissions");
                    toast.error(
                      "Failed to load submissions. Please try again."
                    );
                  }
                } else {
                  dispatch(fetchSubmissionsAction(submissions));
                  Promise.resolve(submissions).then((r) => r);
                }
              } catch (error) {
                toast.error(
                  "Error processing submissions data. Please try again."
                );
              }
            }
          })
          .catch(() => {
            toast.error("Failed to fetch submissions. Please try again.");
          });
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch, logOut, user?.activeOrganizationId, CACHE_DURATION]
  );

  const fetchGroupSubmissions = useCallback(
    (groupId?: string) => {
      if (!groupId) {
        return null;
      }

      // Check if we've fetched recently and use cached data if available
      const now = Date.now();
      if (now - lastFetchTime.current < CACHE_DURATION) {
        return Promise.resolve();
      }

      lastFetchTime.current = now;

      return new Promise(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              query {
                getAllSubmissionsByGroupId(groupId: "${groupId}") {
                  uuid
                  createdAt
                  slug
                  userId
                  authorId
                  assignmentId
                  assignmentLabel
                  privacy
                  color
                  isDraft
                  groupId
                  groupName
                  groupPrivacy
                  isWideData
                  userNameOrEmail
                  category
                  hideFromPatient
                  therapistId
                  isNegative
                  sources
                  icon {
                    name
                    src
                  }
                  responseFields {
                    uuid
                    submissionId
                    sectionId
                    sectionLabel
                    sectionOrder
                    fieldId
                    fieldLabel
                    fieldType
                    value
                    includeInScoreCalculation
                  }
                  submissionScore {
                    scoringInstructions
                    scoringType
                    totalScore
                    color
                    fieldsetScores {
                      name
                      score
                      scoringType
                    }
                    scoreDisplays {
                        id
                        name
                        min
                        max
                        color
                        order
                    }
                    scoreSuffix
                    scoreAsPercentage
                    minPossibleScore
                    maxPossibleScore
                  }
                }
              }
          `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "read_submissions",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (
                error.errors &&
                error.errors[0] &&
                error.errors[0].message.includes("BRKN_6001")
              ) {
                toast.warn(`For security purposes please log in again.`);
                logOut();
              } else {
                toast.error(`Hmm, something went wrong.`);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong (Code: 09AIMN). ${resData.errors[0].message}`
              );
            } else {
              try {
                // Handle chunked responses
                const submissions = resData.data.getAllSubmissionsByGroupId;
                if (submissions.isChunked) {
                  // Process chunked data
                  const fullSubmissions = processChunkedData(submissions);

                  if (fullSubmissions) {
                    dispatch(fetchGroupSubmissionsAction(fullSubmissions));
                    Promise.resolve(fullSubmissions).then((r) => r);
                  } else {
                    console.error(
                      "Failed to process chunked group submissions"
                    );
                    toast.error(
                      "Failed to load group submissions. Please try again."
                    );
                  }
                } else {
                  dispatch(fetchGroupSubmissionsAction(submissions));
                  Promise.resolve(submissions).then((r) => r);
                }
              } catch (error) {
                toast.error(
                  "Error processing group submissions data. Please try again."
                );
              }
            }
          })
          .catch(() => {
            toast.error("Failed to fetch group submissions. Please try again.");
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, logOut]
  );

  const fetchPracticeSubmissions = useCallback(
    () => {
      // Check if we've fetched recently and use cached data if available
      const now = Date.now();
      if (now - lastFetchTime.current < CACHE_DURATION) {
        return Promise.resolve();
      }

      lastFetchTime.current = now;

      return new Promise(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              query {
                getPracticeSubmissions {
                  uuid
                  createdAt
                  slug
                  userId
                  authorId
                  assignmentId
                  assignmentLabel
                  privacy
                  color
                  isDraft
                  groupId
                  groupName
                  groupPrivacy
                  isWideData
                  category
                  hideFromPatient
                  userName
                  userEmail
                  therapistId
                  connectedTherapistId
                  userNameOrEmail
                  isNegative
                  sources
                  icon {
                    name
                    src
                  }
                  responseFields {
                    uuid
                    submissionId
                    sectionId
                    sectionLabel
                    sectionOrder
                    fieldId
                    fieldLabel
                    fieldType
                    value
                    includeInScoreCalculation
                  }
                  submissionScore {
                    scoringInstructions
                    scoringType
                    totalScore
                    color
                    fieldsetScores {
                      name
                      score
                      scoringType
                    }
                    scoreDisplays {
                        id
                        name
                        min
                        max
                        color
                        order
                    }
                    scoreSuffix
                    scoreAsPercentage
                    minPossibleScore
                    maxPossibleScore
                  }
                }
              }
          `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "read_practice_submissions",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (
                error.errors &&
                error.errors[0] &&
                error.errors[0].message.includes("BRKN_6001")
              ) {
                toast.warn(`For security purposes please log in again.`);
                logOut();
              } else {
                toast.error(`Hmm, something went wrong.`);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong (Code: 09AIMN). ${resData.errors[0].message}`
              );
            } else {
              try {
                // Handle chunked responses
                const submissions = resData.data.getPracticeSubmissions;
                if (submissions.isChunked) {
                  // Process chunked data
                  const fullSubmissions = processChunkedData(submissions);

                  if (fullSubmissions) {
                    dispatch(fetchPracticeSubmissionsAction(fullSubmissions));
                    Promise.resolve(fullSubmissions).then((r) => r);
                  } else {
                    console.error("Failed to process chunked submissions");
                    toast.error(
                      "Failed to load submissions. Please try again."
                    );
                  }
                } else {
                  dispatch(fetchPracticeSubmissionsAction(submissions));
                  Promise.resolve(submissions).then((r) => r);
                }
              } catch (error) {
                toast.error(
                  "Error processing submissions data. Please try again."
                );
              }
            }
          })
          .catch(() => {
            toast.error("Failed to fetch submissions. Please try again.");
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, logOut, CACHE_DURATION]
  );

  return {
    fetchAllSubmissions,
    fetchGroupSubmissions,
    fetchPracticeSubmissions,
  };
};

export default useFetchSubmissions;
